import { Injectable } from '@angular/core';
import { CompaniesProvider } from '@modeso/aumico-lib-companies-fe';
import { AuthProvider, GetOrganizationDto, GetUserDto, OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { ICompanyDTO } from '@modeso/types__aumico-lib-companies-be';
import * as countries from 'i18n-iso-countries';
import deCountries from 'i18n-iso-countries/langs/de.json';
import enCountries from 'i18n-iso-countries/langs/en.json';
import frCountries from 'i18n-iso-countries/langs/fr.json';
import itCountries from 'i18n-iso-countries/langs/it.json';
import { Observable, debounceTime, of } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';

const EMISSION_DELAY = 2000;
const DEFAULT_COUNTRY = 'CH';
@Injectable({
    providedIn: 'root',
})
export class CountriesService {
    private user$: Observable<GetUserDto>;
    private organization$: Observable<GetOrganizationDto>;
    private company$: Observable<ICompanyDTO>;
    constructor(
        private authProvider: AuthProvider,
        private companiesProvider: CompaniesProvider,
        private organizationProvider: OrganizationProvider,
    ) {
        this.prepareCountriesWithSupportedLanguages();
        this.user$ = this.authProvider.getCurrentUser$();
        this.organization$ = this.organizationProvider.getCurrentOrganization$();
        this.company$ = this.companiesProvider.getSelectedCompanyById$();
    }

    public getCountriesByLanguage(language: string): countries.LocalizedCountryNames<{ select: 'official' }> {
        const languageWithRegion = language;
        const languageWithoutRegion: string = languageWithRegion.split('-')[0];
        return countries.getNames(languageWithoutRegion);
    }

    public getCountryToExistingCompany$(companyId: string): Observable<string> {
        return this.company$.pipe(
            switchMap((company) => {
                if (company && company.country) {
                    return of(company.country.id);
                } else if (company == null) {
                    this.companiesProvider.dispatchCompanyById(companyId);
                    return this.company$.pipe(
                        switchMap((company) => {
                            if (company && company.country) {
                                return of(company.country.id);
                            } else {
                                return this.getUserCountry$();
                            }
                        }),
                    );
                } else {
                    return this.getUserCountry$();
                }
            }),
        );
    }
    public getCountryForCompanyWithNoCountry$() {
        return this.getUserCountry$();
    }
    public getCountryForAccountingBasis$(companyId: string): Observable<string> {
        this.companiesProvider.dispatchCompanyById(companyId);
        return this.company$.pipe(
            switchMap((company) => {
                if (company && company.country) {
                    return of(company.country.id);
                } else if (company == null) {
                    this.companiesProvider.dispatchCompanyById(companyId);
                    return of(company).pipe(
                        switchMap((company) => {
                            if (company && company.country) {
                                return of(company.country.id);
                            } else {
                                return this.getUserCountry$();
                            }
                        }),
                    );
                } else {
                    return this.getUserCountry$();
                }
            }),
        );
    }
    public getUserCountry$(): Observable<string> {
        return this.user$.pipe(
            switchMap((user) => {
                if (user && user.countryISO31661Alpha2Code) {
                    return of(user.countryISO31661Alpha2Code);
                } else if (user == null) {
                    this.authProvider.dispatchGetCurrentUser();
                    return this.user$.pipe(
                        debounceTime(EMISSION_DELAY),
                        mergeMap((user) => {
                            if (user && user.countryISO31661Alpha2Code) {
                                return of(user.countryISO31661Alpha2Code);
                            } else {
                                return this.getCountryOfOrganization$();
                            }
                        }),
                    );
                } else {
                    return this.getCountryOfOrganization$();
                }
            }),
        );
    }
    private prepareCountriesWithSupportedLanguages() {
        countries.registerLocale(enCountries);
        countries.registerLocale(deCountries);
        countries.registerLocale(frCountries);
        countries.registerLocale(itCountries);
    }

    private getCountryOfOrganization$() {
        return this.organization$.pipe(
            switchMap((organization) => {
                if (organization && organization.countryISO31661Alpha2Code) {
                    return of(organization.countryISO31661Alpha2Code);
                } else if (organization == null) {
                    return of(organization).pipe(
                        switchMap(() => {
                            this.organizationProvider.dispatchGetCurrentOrganization();
                            return this.organization$.pipe(
                                debounceTime(EMISSION_DELAY),
                                switchMap((organization) => {
                                    if (organization && organization.countryISO31661Alpha2Code) {
                                        return of(organization.countryISO31661Alpha2Code);
                                    } else {
                                        return of(DEFAULT_COUNTRY);
                                    }
                                }),
                            );
                        }),
                    );
                } else {
                    return of(DEFAULT_COUNTRY);
                }
            }),
        );
    }
}
